import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

/**
 * The catalog is public and changes rarely, so it's cached generously and
 * fetched without auth — the landing page must render for signed-out visitors.
 */
const CATALOG_STALE_TIME = 5 * 60_000;

/** Endpoints may return either a bare array or `{ plans: [...] }`. */
const unwrap = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export function usePlans() {
  return useQuery({
    queryKey: ['catalog', 'plans'],
    queryFn: () => api.get('/api/catalog/plans', { auth: false }),
    select: (data) => unwrap(data, 'plans'),
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * Global catalog pricing config: { monthlyPrepayDiscountPercent, maxSkipsPerWeek,
 * oneOffDeliveryFee, sampleBoxFee }. `usePlans` deliberately returns a bare
 * array (its `select` unwraps `{ plans }`), so the sibling `monthlyPrepayDiscountPercent`
 * on the plans response can't ride along — this dedicated hook exposes it (and
 * the rest of the config) as an object. Public and cached like the rest.
 */
export function useCatalogConfig() {
  return useQuery({
    queryKey: ['catalog', 'config'],
    queryFn: () => api.get('/api/catalog/config', { auth: false }),
    staleTime: 60_000,
    // Carries the admin's app theme — re-check when the app comes back to the
    // foreground so a theme change reaches people without a restart.
    refetchOnWindowFocus: true,
  });
}

/**
 * The pictorial daily menu — the fruits/veg in a day's box, each with a
 * Hinglish benefit line. Defaults to TOMORROW's box when no date is given
 * (matching the API). Returns the items array; an unplanned day yields [].
 * Public and cached like the rest of the catalog.
 */
export function useDailyMenu(date) {
  return useQuery({
    queryKey: ['catalog', 'daily-menu', date ?? 'tomorrow'],
    queryFn: () =>
      api.get(`/api/catalog/daily-menu${date ? `?date=${date}` : ''}`, { auth: false }),
    select: (data) => data?.items ?? [],
    staleTime: CATALOG_STALE_TIME,
  });
}

/** The shop's shelves, with how many products each holds. Public and cached. */
export function useCategories() {
  return useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => api.get('/api/catalog/categories', { auth: false }),
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useProducts() {
  return useQuery({
    queryKey: ['catalog', 'products'],
    queryFn: () => api.get('/api/catalog/products', { auth: false }),
    select: (data) => unwrap(data, 'products'),
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * Delivery time windows for the slot pickers. Public and cached like the rest
 * of the catalog — the API returns them earliest-first with exactly one
 * `is_default` (the morning window), which the pickers preselect.
 */
/**
 * Delivery days and windows that are actually bookable now, with the same-day
 * cut-off applied server-side. Deliberately short-lived: which slots are still
 * open changes with the clock, so a cached answer goes stale on its own. Also
 * refetched when the app regains focus, since a phone left open overnight would
 * otherwise still be offering yesterday's slots.
 */
export function useDeliveryOptions() {
  return useQuery({
    queryKey: ['catalog', 'delivery-options'],
    queryFn: () => api.get('/api/catalog/delivery-options', { auth: false }),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSlots() {
  return useQuery({
    queryKey: ['catalog', 'slots'],
    queryFn: () => api.get('/api/catalog/slots', { auth: false }),
    select: (data) => unwrap(data, 'slots'),
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * Products are addressed by slug in the URL. There's no per-slug endpoint in
 * the contract, so we resolve against the cached list rather than adding a
 * round trip — the list is already warm from the catalog page.
 */
export function useProduct(slug) {
  const query = useProducts();
  return {
    ...query,
    data: query.data?.find((product) => product.slug === slug),
    notFound: query.isSuccess && !query.data?.some((product) => product.slug === slug),
  };
}
